import React from "react";
import * as CSS from "csstype";

import {
  Badge,
  Box,
  Button,
  Center,
  HStack,
  Spacer,
  Stack,
  StackDirection,
  StatLabel,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";

import { FileUri } from "api";
import Card from "components/Card";
import ContractStat from "components/ContractStat";
import Empty from "components/Empty";
import LoadingSpinner from "components/LoadingSpinner";
import SortTermsSelect from "components/SortTermsSelect";
import { ActiveSort } from "contexts/RequestParamsProvider";
import useContractDetails from "hooks/api/contracts/useContractDetails";
import useContractFiles from "hooks/api/contracts/useContractFiles";
import useDownloadFile from "hooks/useDownloadFile";
import { SORT_DIRECTION } from "hooks/useSort";
import { DownloadIcon } from "icons";
import { formatDate } from "util/statHelpers";
import { detailsBodyPadding, mobileStyleBreakpoint } from "util/styleHelpers";

const FileListItemCard = ({
  file,
}: {
  file: ContractFile;
}): React.ReactElement => {
  const downloadDocumentFile = useDownloadFile();

  return (
    <Card p={4} borderBottom="1px solid " borderColor="grey.200">
      <HStack align="flex-start" width="100%">
        <ContractStat
          label={
            <HStack>
              <StatLabel whiteSpace="nowrap">File Name</StatLabel>
              <Spacer flex={1} />
              <Text>{formatDate(file.date)}</Text>
            </HStack>
          }
        >
          <Button
            variant="link"
            onClick={() => downloadDocumentFile(file)}
            textAlign="left"
            wordBreak="break-all"
            whiteSpace="break-spaces"
          >
            <HStack spacing={1} align="flex-start">
              <Text color="inherit" fontWeight="semibold" textStyle="body2">
                {file.filename}
              </Text>
              <DownloadIcon />
            </HStack>
          </Button>
        </ContractStat>
      </HStack>
    </Card>
  );
};

const fileSubTabPadding = mobileStyleBreakpoint(0, 1);
const fileSubTabHeadingPaddingY = mobileStyleBreakpoint(4, 7);
const fileTabPadding = mobileStyleBreakpoint(0, 7);
const fileSubTabHeadingDirection = mobileStyleBreakpoint<StackDirection>(
  "column",
  "row"
) as StackDirection;
const fileSubTabHeadingAlign = mobileStyleBreakpoint<CSS.Property.AlignItems>(
  "flex-start",
  "center"
);

const emptyMaxWidth = mobileStyleBreakpoint<CSS.Property.Height>("75%", "50%");

const fileTabSortOptions = [
  {
    sortKey: "Date",
    label: "Upload Date",
  },
];

const FilesTabPanel = ({
  files,
  loading = false,
  category,
}: {
  files?: ContractFile[];
  category: string;
  loading?: boolean;
}): React.ReactElement => {
  if (files && !loading) {
    return files.length > 0 ? (
      <Stack spacing={3}>
        {files.map((file) => (
          <FileListItemCard key={file.filename} file={file} />
        ))}
      </Stack>
    ) : (
      <Center minHeight="400px">
        <Empty
          maxWidth={emptyMaxWidth}
          title={`No ${category} files`}
          message={`No ${category} files...yet.`}
        />
      </Center>
    );
  }
  return <LoadingSpinner />;
};

const FilesTab = (): React.ReactElement => {
  const { data: contract } = useContractDetails();
  const [activeSort, setActiveSort] = React.useState<ActiveSort<FileUri>>();
  const { data: files, isLoading } = useContractFiles(
    contract?.docEntry,
    activeSort
  );

  const onChangeSort = React.useCallback(
    (key: keyof FileUri, direction: SORT_DIRECTION | undefined) =>
      setActiveSort(key && direction ? { key, direction } : undefined),
    []
  );
  return (
    <Tabs
      flex={1}
      overflow="hidden"
      colorScheme="black"
      display="flex"
      flexDirection="column"
      isLazy={true}
      variant="line"
      size="sm"
      px={fileTabPadding}
      mt={1}
      pt={detailsBodyPadding}
    >
      <Stack
        direction={fileSubTabHeadingDirection}
        align={fileSubTabHeadingAlign}
        px={4}
        py={fileSubTabHeadingPaddingY}
      >
        <HStack>
          <TabList style={{ borderBottom: "none" }}>
            <Tab px={0} mx={2}>
              <HStack spacing={1}>
                <Text textStyle="body2" color="grey.500" fontWeight="semibold">
                  Contracts
                </Text>
                {files?.contracts && files?.contracts.length > 0 && (
                  <Badge colorScheme="grey">{files?.contracts.length}</Badge>
                )}
              </HStack>
            </Tab>
            <Tab px={0} mx={2}>
              <HStack spacing={1}>
                <Text textStyle="body2" color="grey.500" fontWeight="semibold">
                  Statements
                </Text>
                {files?.statements && files?.statements.length > 0 && (
                  <Badge colorScheme="grey">{files?.statements.length}</Badge>
                )}
              </HStack>
            </Tab>
          </TabList>
        </HStack>
        <Spacer flex={1} />
        <Box>
          <SortTermsSelect
            sortOptions={fileTabSortOptions}
            selectedSortKey={activeSort?.key}
            selectedDirection={activeSort?.direction}
            onChange={onChangeSort}
          />
        </Box>
      </Stack>
      <TabPanels overflowY="auto" flex={1}>
        <TabPanel p={fileSubTabPadding}>
          <FilesTabPanel
            files={files?.contracts}
            loading={isLoading}
            category="contract"
          />
        </TabPanel>
        <TabPanel p={fileSubTabPadding}>
          <FilesTabPanel
            files={files?.statements}
            loading={isLoading}
            category="statement"
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default FilesTab;
